import { fromJS, is, Map } from 'immutable';
import { isImmutable } from 'pluginServices/helpers/immutableHelpers';
import PathwayResolver from 'resolvers/RTMResolvers/PathwayResolver';
import ActivityService from 'services/ActivityService';
import * as constants from './constants';
import { CONFIG_CHANGE } from './pathwayChangeTypes';

const keyPaths = {
  config: ['config'],
};

export default (() => {
  let pathwayResolver;
  let isRegistered = false;

  let _pathwayCache = Map();
  const _subscriptions = {};

  function _createUnsubscribe(pathwayId, listener) {
    return () => {
      const listenerIndex = _subscriptions[pathwayId].indexOf(listener);
      _subscriptions[pathwayId].splice(listenerIndex, 1);
      if (_subscriptions[pathwayId].length === 0) {
        _forgetPathway(pathwayId);
      }
    };
  }

  function _notifyListeners(pathwayId) {
    if (_subscriptions[pathwayId]) {
      const listeners = _subscriptions[pathwayId];
      const updatedPathwayObject = _pathwayCache.get(pathwayId).toJS();
      listeners.forEach((listener) => {
        listener(updatedPathwayObject);
      });
    }
  }

  function _forgetPathway(pathwayId) {
    _pathwayCache = _pathwayCache.remove(pathwayId);
  }

  function _saveToCache({ config, ...pathway }) {
    let parsedConfig = {};
    if (config && typeof config === 'string') {
      parsedConfig = JSON.parse(config);
    }

    _pathwayCache = _pathwayCache.set(pathway.pathwayId, fromJS({
      ...pathway,
      config: parsedConfig,
    }));
  }

  // Create

  const createPathway = async (activityId, keyPath, pathwayType, preload) => {
    const pathwayTypeWithDefault = pathwayType || constants.PATHWAY_TYPE_LINEAR;
    const payload = {
      activityId,
      pathwayType: pathwayTypeWithDefault,
      preloadPathway: preload ? preload.toUpperCase() : 'NONE',
    };
    const newPathway = await pathwayResolver.authorPathwayCreate(payload);
    _saveToCache(newPathway);

    const pathwayRef = {
      pathwayId: newPathway.pathwayId,
      pathwayType: newPathway.pathwayType,
    };
    await ActivityService.saveInActivityConfig(activityId, keyPath, pathwayRef);

    return newPathway.pathwayId;
  };

  // Get

  const getPathway = async (pathwayId, callback) => {
    if (!pathwayId) {
      return null;
    }
    if (!_pathwayCache.get(pathwayId)) {
      const pathway = await pathwayResolver.authorPathwayGet({ pathwayId });
      _saveToCache(pathway);
    }

    const pathway = _pathwayCache.get(pathwayId).toJS();
    if (callback) { callback(pathway); }
    return pathway;
  };

  // Delete

  const deletePathway = async (parentActivityId, keyPath) => {
    const pathway = await ActivityService.getInActivityConfig(parentActivityId, keyPath);
    const { pathwayId } = pathway;
    _forgetPathway(pathwayId);

    await ActivityService.saveInActivityConfig(parentActivityId, keyPath, undefined);

    const payload = { pathwayId, parentActivityId };
    pathwayResolver.authorPathwayDelete(payload);
  };

  // Subscribe

  function subscribeToPathway(pathwayId, listener) {
    _subscriptions[pathwayId] = _subscriptions[pathwayId] || [];
    _subscriptions[pathwayId].push(listener);

    if (_pathwayCache.get(pathwayId)) {
      listener(_pathwayCache.get(pathwayId).toJS());
    } else {
      getPathway(pathwayId, listener);
    }

    return _createUnsubscribe(pathwayId, listener);
  }

  async function notifyUpdate(pathwayId) {
    const pathway = await pathwayResolver.authorPathwayGet({ pathwayId });
    _saveToCache(pathway);
    _notifyListeners(pathwayId);
    ActivityService.notifyUpdate(pathway.parentActivityId);
  }

  // ReOrder

  async function reorderPathwayWalkables(pathwayId, walkableIds) {
    // First, anticipate a successful result and change the cache immediately to avoid a temporary UI mismatch
    // If the server call fails the 'notifyUpdate' call will reset the cache anyway, so this should be safe.
    const cachePathway = _pathwayCache.get(pathwayId);
    if (cachePathway) {
      const pathway = cachePathway.toJS();
      const reorderedChildren = walkableIds.map((walkableId) => pathway.children.find(({ elementId }) => walkableId === elementId));
      _saveToCache({ ...pathway, children: reorderedChildren });
      _notifyListeners(pathwayId);
    }

    await pathwayResolver.authorPathwayWalkableReorder({ pathwayId, walkableIds });
    notifyUpdate(pathwayId);
  }

  async function savePathwayConfig(pathwayId, newConfig) {
    _dangerouslySetConfigInCache(pathwayId, newConfig);
    await pathwayResolver.authorPathwayConfigReplace({ pathwayId, config: JSON.stringify(newConfig) });
    return pathwayId;
  }

  async function saveInPathwayConfig(pathwayId, keyPath, newConfigPart) {
    if (!_pathwayCache.has(pathwayId)) {
      return null;
    }
    const oldConfig = fromJS(_pathwayCache.getIn([pathwayId, ...keyPaths.config]) || {});
    const newConfig = oldConfig.setIn(keyPath, newConfigPart);
    return savePathwayConfig(pathwayId, newConfig);
  }

  // This is how we can get an invalid cache (not waiting for backend response)
  function _dangerouslySetConfigInCache(pathwayId, newConfig) {
    const jsConfig = isImmutable(newConfig) ? newConfig.toJS() : newConfig;
    const immutableConfig = fromJS(jsConfig);

    // save and notify only if the interactive config is different to what we already have
    const oldConfig = _pathwayCache.getIn([pathwayId, ...keyPaths.config]);
    if (!is(oldConfig, immutableConfig)) {
      _pathwayCache = _pathwayCache.setIn([pathwayId, ...keyPaths.config], immutableConfig);
      _notifyListeners(pathwayId);
    }
  }

  function receivePathwayChange(broadcast) {
    const {
      elementId: pathwayId,
      action,
      config,
    } = broadcast.response;

    if (action === CONFIG_CHANGE && _pathwayCache.has(pathwayId)) {
      try {
        _dangerouslySetConfigInCache(pathwayId, JSON.parse(config));
      } catch (error) {
        throw Error(`Could not parse and save a broadcast config change on pathway ${pathwayId}`);
      }
    }
  }

  return {
    isRegistered: () => isRegistered,
    register: (connectionProvider) => {
      isRegistered = true;
      pathwayResolver = new PathwayResolver(connectionProvider);
    },
    createPathway,
    getPathway,
    deletePathway,
    reorderPathwayWalkables,
    savePathwayConfig,
    saveInPathwayConfig,

    receivePathwayChange,
    subscribeToPathway,
    notifyUpdate,
  };
})();
